import { type FC, useState, useMemo, useEffect } from 'react'
import { DataTable } from '@/components/data-table/DataTable'
import { transactionColumns } from '@/components/dashboard/TransactionColumns'
import {
    type Transaction,
    useTransactions,
    useDownloadReceipt,
    useDownloadMultipleReceipts,
} from '@/hooks/useTransactions'
import { TransactionDetailsModal } from '@/components/transactions/TransactionDetailsModal'
import { TransactionsFilterDialog } from '@/components/transactions/TransactionsFilterDialog'
import { QueuedTransactionsTab } from '@/components/transactions/QueuedTransactionsTab'
import type { TransactionFilters } from '@/types/transaction'
import { cn } from '@/lib/utils'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'

type TabType = 'all' | 'queued'

const tabs = [
    { id: 'all' as const, label: 'All transactions' },
    { id: 'queued' as const, label: 'Awaiting liquidity' },
]

const Transactions: FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('all')
    const [selectedTransaction, setSelectedTransaction] =
        useState<Transaction | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false)

    // Transaction filters state
    const [filters, setFilters] = useState<TransactionFilters>({
        page: 1,
        limit: 20,
    })

    // Fetch transactions with filters
    const {
        data: transactionsData,
        isLoading,
        error,
        refetch,
    } = useTransactions(filters)

    const downloadReceipt = useDownloadReceipt()
    const downloadMultipleReceipts = useDownloadMultipleReceipts()

    // Show toast notification on error
    useEffect(() => {
        if (error) {
            toast.error('Failed to load transactions', {
                description:
                    error instanceof Error
                        ? error.message
                        : 'An unexpected error occurred',
                action: {
                    label: 'Retry',
                    onClick: () => refetch(),
                },
            })
        }
    }, [error, refetch])

    const handleViewTransaction = (transaction: Transaction) => {
        setSelectedTransaction(transaction)
        setIsModalOpen(true)
    }

    const handleDownloadReceipt = (transactions: Transaction[]) => {
        if (transactions.length === 0) return

        if (transactions.length === 1) {
            downloadReceipt.mutate(transactions[0].id, {
                onSuccess: () =>
                    toast.success('Receipt downloaded successfully'),
                onError: () => toast.error('Failed to download receipt'),
            })
        } else {
            const toastId = toast.loading(
                `Downloading ${transactions.length} receipts...`
            )
            downloadMultipleReceipts.mutate(
                transactions.map(t => t.id),
                {
                    onSuccess: () => {
                        toast.dismiss(toastId)
                        toast.success(
                            `${transactions.length} receipts downloaded successfully`
                        )
                    },
                    onError: () => {
                        toast.dismiss(toastId)
                        toast.error('Failed to download receipts')
                    },
                }
            )
        }
    }

    const handleRetry = () => {
        refetch()
    }

    const handleApplyFilters = (newFilters: TransactionFilters) => {
        setFilters({ ...newFilters, page: 1, limit: filters.limit })
    }

    // Transform API transactions to match the legacy Transaction type
    const transactions = useMemo<Transaction[]>(() => {
        if (!transactionsData?.data || !Array.isArray(transactionsData.data))
            return []

        return transactionsData.data.map(txn => ({
            id: txn.transactionId,
            date: new Date(txn.date).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }),
            accountName: txn.accountName,
            walletAddress: txn.walletAddress,
            txnType: (txn.transactionType === 'CREDIT'
                ? 'Receive'
                : 'Send') as Transaction['txnType'],
            amount: parseFloat(txn.amountToken.split(' ')[0] || '0'),
            token: txn.currency.toUpperCase() as Transaction['token'],
            status:
                txn.status === 'COMPLETED'
                    ? 'Completed'
                    : txn.status === 'PENDING'
                      ? 'Pending'
                      : txn.status === 'CANCELLED'
                        ? 'Cancelled'
                        : 'Failed',
        }))
    }, [transactionsData])

    const allTransactions = error ? (
        <div className="flex items-center justify-center h-[400px]">
            <div className="text-center space-y-4">
                <div className="flex justify-center">
                    <div className="rounded-full bg-red-100 p-3">
                        <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                </div>
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold">
                        Failed to load transactions
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                        {error instanceof Error
                            ? error.message
                            : 'An unexpected error occurred while fetching transactions.'}
                    </p>
                </div>
                <Button
                    onClick={handleRetry}
                    variant="outline"
                    className="gap-2"
                >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                </Button>
            </div>
        </div>
    ) : isLoading ? (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Skeleton className="h-10 w-[300px]" />
                <Skeleton className="h-10 w-[100px]" />
            </div>
            <div className="rounded-md border">
                <Skeleton className="h-12 w-full rounded-t-md" />
                {Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                ))}
            </div>
            <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-[200px]" />
                <Skeleton className="h-8 w-[300px]" />
            </div>
        </div>
    ) : (
        <DataTable
            columns={transactionColumns}
            data={transactions}
            searchPlaceholder="Search using transaction ID"
            onViewRow={handleViewTransaction}
            onDownloadReceipt={handleDownloadReceipt}
            onFilter={() => setIsFilterDialogOpen(true)}
        />
    )

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-2xl font-semibold">Transactions</h1>
                <p className="text-sm text-muted-foreground">
                    Manage and track all transactions initiated on Ogaboss.
                </p>
            </div>

            {/* Tabs Navigation */}
            <div className="overflow-x-auto">
                <div className="flex min-w-max gap-6 border-b border-border pb-0">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                                'relative whitespace-nowrap pb-3 text-sm font-medium transition-colors',
                                activeTab === tab.id
                                    ? 'text-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {tab.label}
                            {activeTab === tab.id && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#17A2B8]" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === 'all' ? allTransactions : <QueuedTransactionsTab />}

            {/* Transaction Details Modal */}
            <TransactionDetailsModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                transaction={selectedTransaction}
            />

            {/* Transactions Filter Dialog */}
            <TransactionsFilterDialog
                open={isFilterDialogOpen}
                onOpenChange={setIsFilterDialogOpen}
                currentFilters={filters}
                onApplyFilters={handleApplyFilters}
            />
        </div>
    )
}

export default Transactions
